import {
  AppstoreOutlined,
  BarsOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  FilterOutlined,
  InboxOutlined,
  ReloadOutlined,
  WarningOutlined
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message
} from "antd";
import type { UploadFile } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { transportApi } from "../../api/transportApi";
import type { AttachmentDto, AttachmentZombieDto } from "../../types/api";
import { getApiErrorMessage } from "../../utils/apiError";
import { LookupSelect } from "../../shared/components/LookupSelect";

const { Dragger } = Upload;
const { Text, Title } = Typography;

// ─── constants ────────────────────────────────────────────────────────────────
const ENTITY_TYPES = [
  { value: "consignment", label: "Consignment" },
  { value: "manifest", label: "Manifest" },
  { value: "trip", label: "Trip" },
  { value: "vehicle", label: "Vehicle" },
  { value: "driver", label: "Driver" },
  { value: "lorryhire", label: "Lorry Hire" },
  { value: "other", label: "Other" }
];

const DOCUMENT_TYPES = [
  { value: "LR", label: "LR / Consignment Note" },
  { value: "Invoice", label: "Invoice" },
  { value: "POD", label: "POD (Proof of Delivery)" },
  { value: "EWayBill", label: "E-Way Bill" },
  { value: "Insurance", label: "Insurance Certificate" },
  { value: "RCBook", label: "RC Book" },
  { value: "DrivingLicense", label: "Driving License" },
  { value: "Permit", label: "Vehicle Permit" },
  { value: "FitnessNCertificate", label: "Fitness Certificate" },
  { value: "Weighment", label: "Weighment Slip" },
  { value: "GatePass", label: "Gate Pass" },
  { value: "Receipt", label: "Receipt / Voucher" },
  { value: "Contract", label: "Contract / Agreement" },
  { value: "Photo", label: "Photo Evidence" },
  { value: "Other", label: "Other" }
];

const ACCEPT_TYPES = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx";

// ─── helpers ──────────────────────────────────────────────────────────────────
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function FileIcon({ ext, size = 20 }: { ext: string; size?: number }) {
  const e = ext.toLowerCase().replace(".", "");
  const style = { fontSize: size };
  if (["pdf"].includes(e)) return <FilePdfOutlined style={{ ...style, color: "#e74c3c" }} />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(e)) return <FileImageOutlined style={{ ...style, color: "#27ae60" }} />;
  if (["xls", "xlsx"].includes(e)) return <FileExcelOutlined style={{ ...style, color: "#1d6f42" }} />;
  if (["doc", "docx"].includes(e)) return <FileTextOutlined style={{ ...style, color: "#2980b9" }} />;
  return <FileUnknownOutlined style={{ ...style, color: "#95a5a6" }} />;
}

function EntityBadge({ entityType, entityId }: { entityType: string; entityId: number }) {
  const colors: Record<string, string> = {
    consignment: "blue", manifest: "purple", trip: "green",
    vehicle: "orange", driver: "cyan", lorryhire: "magenta", other: "default"
  };
  const labels: Record<string, string> = {
    consignment: "CNS", manifest: "MFT", trip: "TRIP",
    vehicle: "VEH", driver: "DRV", lorryhire: "LH", other: "OTH"
  };
  return (
    <Tag color={colors[entityType] ?? "default"}>
      {labels[entityType] ?? entityType.toUpperCase()} #{entityId}
    </Tag>
  );
}

// ─── component ────────────────────────────────────────────────────────────────
export const AttachmentListPage = () => {
  // ── list state ──────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<AttachmentDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewMode, setViewMode] = useState<"grid" | "table">("table");

  // ── filters ─────────────────────────────────────────────────────────────────
  const [searchQ, setSearchQ] = useState("");
  const [filterBranchId, setFilterBranchId] = useState<number | undefined>();
  const [filterEntityType, setFilterEntityType] = useState<string | undefined>();
  const [filterDocType, setFilterDocType] = useState<string | undefined>();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // ── upload drawer ────────────────────────────────────────────────────────────
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadForm] = Form.useForm();

  // ── edit drawer ──────────────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<AttachmentDto | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();

  // ── preview modal ────────────────────────────────────────────────────────────
  const [previewTarget, setPreviewTarget] = useState<AttachmentDto | null>(null);

  // ── admin / zombie panel ─────────────────────────────────────────────────────
  const [zombies, setZombies] = useState<AttachmentZombieDto[]>([]);
  const [zombieLoading, setZombieLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  // ── lookups ──────────────────────────────────────────────────────────────────
  const branchFetcher = useCallback(
    (q: string, _p: number, pageSize: number) => transportApi.searchBranchLookups(q, 1, pageSize),
    []
  );

  // ── data loading ──────────────────────────────────────────────────────────────
  const load = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const result = await transportApi.listAttachments({
        page: p, pageSize: ps,
        q: searchQ.trim() || undefined,
        branchId: filterBranchId,
        entityType: filterEntityType,
        documentType: filterDocType
      });
      setRows(result.data);
      setTotal(result.total);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load attachments"));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQ, filterBranchId, filterEntityType, filterDocType]);

  useEffect(() => { void load(page, pageSize); }, []);

  const reload = () => {
    setPage(1);
    void load(1, pageSize);
  };

  // ── upload ────────────────────────────────────────────────────────────────────
  const onUpload = async () => {
    const values = await uploadForm.validateFields();
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file) { message.warning("Select a file first"); return; }

    setUploading(true);
    try {
      await transportApi.uploadAttachment({
        module: "transport",
        entityType: values.entityType,
        entityId: values.entityId,
        documentType: values.documentType,
        branchId: values.branchId,
        tagsJson: values.notes ? JSON.stringify({ notes: values.notes }) : undefined,
        file
      });
      message.success("Document uploaded successfully");
      setUploadOpen(false);
      setFileList([]);
      uploadForm.resetFields();
      reload();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────────
  const onDelete = async (id: string) => {
    try {
      await transportApi.deleteAttachment(id);
      message.success("Document deleted");
      reload();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Delete failed"));
    }
  };

  // ── edit metadata ─────────────────────────────────────────────────────────────
  const openEdit = (row: AttachmentDto) => {
    setEditTarget(row);
    const notes = row.tagsJson ? (() => { try { return JSON.parse(row.tagsJson!).notes ?? ""; } catch { return row.tagsJson ?? ""; } })() : "";
    editForm.setFieldsValue({ documentType: row.documentType, notes });
  };

  const onSaveEdit = async () => {
    if (!editTarget) return;
    const values = await editForm.validateFields();
    setEditSaving(true);
    try {
      await transportApi.updateAttachmentMetadata(editTarget.id, {
        documentType: values.documentType,
        tagsJson: values.notes ? JSON.stringify({ notes: values.notes }) : undefined
      });
      message.success("Updated");
      setEditTarget(null);
      reload();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Update failed"));
    } finally {
      setEditSaving(false);
    }
  };

  // ── zombies ───────────────────────────────────────────────────────────────────
  const loadZombies = async () => {
    setZombieLoading(true);
    try {
      const data = await transportApi.listZombieAttachments();
      setZombies(data);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load zombie files"));
    } finally {
      setZombieLoading(false);
    }
  };

  const onPurge = async () => {
    setPurging(true);
    try {
      const result = await transportApi.purgeZombieAttachments();
      message.success(result.message);
      setZombies([]);
      reload();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Purge failed"));
    } finally {
      setPurging(false);
    }
  };

  // ── preview ───────────────────────────────────────────────────────────────────
  const isPreviewable = (row: AttachmentDto) => {
    const ext = row.fileExtension.toLowerCase().replace(".", "");
    return ["jpg", "jpeg", "png", "pdf"].includes(ext);
  };

  // ── table columns ──────────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: "File",
      key: "file",
      width: 280,
      render: (_: unknown, row: AttachmentDto) => (
        <Space size={8}>
          <FileIcon ext={row.fileExtension} size={22} />
          <div>
            <div style={{ fontWeight: 500, fontSize: 13, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row.fileName}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>{formatFileSize(row.fileSize)}</Text>
          </div>
        </Space>
      )
    },
    {
      title: "Branch",
      key: "branch",
      width: 90,
      render: (_: unknown, row: AttachmentDto) =>
        row.branchCode
          ? <Tag color="purple">{row.branchCode}</Tag>
          : <Text type="secondary">—</Text>
    },
    {
      title: "Entity",
      key: "entity",
      width: 140,
      render: (_: unknown, row: AttachmentDto) => <EntityBadge entityType={row.entityType} entityId={row.entityId} />
    },
    {
      title: "Document Type",
      dataIndex: "documentType",
      width: 150,
      render: (v: string) => {
        const match = DOCUMENT_TYPES.find(d => d.value === v);
        return <Tag>{match?.label ?? v}</Tag>;
      }
    },
    {
      title: "Uploaded",
      key: "uploaded",
      width: 130,
      render: (_: unknown, row: AttachmentDto) => (
        <div>
          <div style={{ fontSize: 12 }}>{dayjs(row.uploadedAt).format("DD MMM YY")}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.uploadedBy}</Text>
        </div>
      )
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_: unknown, row: AttachmentDto) => (
        <Space size={4}>
          {isPreviewable(row) && (
            <Tooltip placement="bottom" title="Preview">
              <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewTarget(row)} />
            </Tooltip>
          )}
          <Tooltip placement="bottom" title="Download">
            <Button size="small" icon={<DownloadOutlined />} href={row.downloadUrl} target="_blank" />
          </Tooltip>
          <Tooltip placement="bottom" title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Tooltip placement="bottom" title="Delete">
            <Popconfirm
              title="Delete this document?"
              description="It will be soft-deleted and can be purged later."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void onDelete(row.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ], []);

  // ── grid card ──────────────────────────────────────────────────────────────────
  const GridCard = ({ row }: { row: AttachmentDto }) => (
    <Card
      size="small"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
      styles={{ body: { flex: 1, display: "flex", flexDirection: "column", gap: 8 } }}
      actions={[
        isPreviewable(row) && (
          <Tooltip placement="bottom" key="preview" title="Preview">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setPreviewTarget(row)} />
          </Tooltip>
        ),
        <Tooltip placement="bottom" key="download" title="Download">
          <Button type="text" size="small" icon={<DownloadOutlined />} href={row.downloadUrl} target="_blank" />
        </Tooltip>,
        <Tooltip placement="bottom" key="edit" title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        </Tooltip>,
        <Popconfirm
          key="delete"
          title="Delete this document?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => void onDelete(row.id)}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ].filter(Boolean)}
    >
      <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
        <FileIcon ext={row.fileExtension} size={40} />
      </div>
      <Text
        style={{ fontWeight: 500, fontSize: 13, textAlign: "center", wordBreak: "break-all" }}
        ellipsis={{ tooltip: row.fileName }}
      >
        {row.fileName}
      </Text>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <EntityBadge entityType={row.entityType} entityId={row.entityId} />
        {row.branchCode && <Tag color="purple">{row.branchCode}</Tag>}
      </div>
      <Tag style={{ alignSelf: "flex-start" }}>
        {DOCUMENT_TYPES.find(d => d.value === row.documentType)?.label ?? row.documentType}
      </Tag>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {formatFileSize(row.fileSize)} · {dayjs(row.uploadedAt).format("DD MMM YY")}
      </Text>
    </Card>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card className="panel-card">
      {/* ── Header ── */}
      <div className="panel-title-row" style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Document Vault</Title>
        <Space>
          <Button
            icon={viewMode === "table" ? <AppstoreOutlined /> : <BarsOutlined />}
            onClick={() => setViewMode(v => v === "table" ? "grid" : "table")}
          >
            {viewMode === "table" ? "Grid" : "Table"}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reload}>Refresh</Button>
          <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>
            Upload Document
          </Button>
        </Space>
      </div>

      {/* ── Filters ── */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: "10px 12px" } }}
        extra={
          <Button
            type="link"
            size="small"
            icon={<FilterOutlined />}
            onClick={() => setFiltersExpanded(e => !e)}
          >
            {filtersExpanded ? "Hide Filters" : "Show Filters"}
          </Button>
        }
      >
        <Space wrap style={{ width: "100%" }}>
          <Input.Search
            placeholder="Search filename, entity, document type..."
            allowClear
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onSearch={reload}
            style={{ width: 340 }}
          />
          {filtersExpanded && (
            <>
              <LookupSelect
                fetcher={branchFetcher}
                modalTitle="Select Branch"
                placeholder="Filter by branch..."
                value={filterBranchId}
                onChange={(v) => setFilterBranchId(v as number | undefined)}
                style={{ width: 200 }}
                allowClear
              />
              <Select
                placeholder="Entity type"
                allowClear
                value={filterEntityType}
                onChange={setFilterEntityType}
                options={ENTITY_TYPES}
                style={{ width: 150 }}
              />
              <Select
                placeholder="Document type"
                allowClear
                value={filterDocType}
                onChange={setFilterDocType}
                options={DOCUMENT_TYPES}
                style={{ width: 200 }}
              />
              <Button onClick={() => { setFilterBranchId(undefined); setFilterEntityType(undefined); setFilterDocType(undefined); setSearchQ(""); reload(); }}>
                Clear All
              </Button>
            </>
          )}
        </Space>
      </Card>

      {/* ── Content ── */}
      {viewMode === "table" ? (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
            showTotal: (t, [s, e]) => `${s}-${e} of ${t} documents`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); void load(p, ps); }
          }}
        />
      ) : (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}><Spin size="large" /></div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "#999" }}>
              <InboxOutlined style={{ fontSize: 48 }} />
              <div style={{ marginTop: 12 }}>No documents found</div>
            </div>
          ) : (
            <Row gutter={[12, 12]}>
              {rows.map(row => (
                <Col key={row.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                  <GridCard row={row} />
                </Col>
              ))}
            </Row>
          )}
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button disabled={page <= 1} onClick={() => { setPage(p => { const np = p - 1; void load(np, pageSize); return np; })} }>Previous</Button>
            <Text style={{ margin: "0 12px" }}>Page {page}</Text>
            <Button disabled={rows.length < pageSize} onClick={() => { setPage(p => { const np = p + 1; void load(np, pageSize); return np; })}}>Next</Button>
          </div>
        </div>
      )}

      {/* ── Admin: Zombie Cleanup ── */}
      <Collapse
        style={{ marginTop: 24 }}
        items={[{
          key: "zombies",
          label: (
            <Space>
              <WarningOutlined style={{ color: "#e67e22" }} />
              <span>Zombie File Cleanup</span>
              {zombies.length > 0 && <Badge count={zombies.length} color="orange" />}
            </Space>
          ),
          children: (
            <div>
              <Alert
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message="Zombie files are failed uploads, stuck pending uploads (>2h), or soft-deleted records older than 7 days."
                style={{ marginBottom: 12 }}
              />
              <Space style={{ marginBottom: 12 }}>
                <Button icon={<ReloadOutlined />} onClick={loadZombies} loading={zombieLoading}>
                  Scan for Zombies
                </Button>
                {zombies.length > 0 && (
                  <Popconfirm
                    title={`Permanently delete ${zombies.length} zombie file(s)?`}
                    description="This removes both the database records and storage files. This cannot be undone."
                    okText="Purge All"
                    okButtonProps={{ danger: true }}
                    onConfirm={onPurge}
                  >
                    <Button danger icon={<DeleteOutlined />} loading={purging}>
                      Purge {zombies.length} Zombie(s)
                    </Button>
                  </Popconfirm>
                )}
              </Space>
              {zombies.length > 0 && (
                <Table
                  rowKey="id"
                  size="small"
                  loading={zombieLoading}
                  dataSource={zombies}
                  pagination={false}
                  columns={[
                    {
                      title: "File",
                      key: "file",
                      render: (_: unknown, z: AttachmentZombieDto) => (
                        <Space size={6}>
                          <FileUnknownOutlined />
                          <Text style={{ fontSize: 12 }}>{z.fileName}</Text>
                        </Space>
                      )
                    },
                    { title: "Reason", dataIndex: "reason", width: 180, render: (v: string) => <Tag color="orange">{v}</Tag> },
                    { title: "Status", dataIndex: "status", width: 100 },
                    { title: "Size", dataIndex: "fileSize", width: 90, render: (v: number) => formatFileSize(v) },
                    { title: "Uploaded", dataIndex: "uploadedAt", width: 110, render: (v: string) => dayjs(v).format("DD MMM YY HH:mm") },
                    { title: "Path", dataIndex: "storagePath", render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> }
                  ]}
                />
              )}
              {zombies.length === 0 && !zombieLoading && (
                <Text type="secondary">Click "Scan for Zombies" to check for orphaned files.</Text>
              )}
            </div>
          )
        }]}
      />

      {/* ── Upload Drawer ── */}
      <Drawer
        title={<Space><CloudUploadOutlined /> Upload Document</Space>}
        open={uploadOpen}
        onClose={() => { setUploadOpen(false); setFileList([]); uploadForm.resetFields(); }}
        width={560}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => { setUploadOpen(false); setFileList([]); uploadForm.resetFields(); }}>Cancel</Button><Button type="primary" loading={uploading} onClick={onUpload}>{uploading ? "Uploading..." : "Upload Document"}</Button></div>}
        maskClosable={false}
      >
        <Form form={uploadForm} layout="vertical" initialValues={{ entityType: "consignment", documentType: "LR" }}>
          <Form.Item label="Select File" required>
            <Dragger
              accept={ACCEPT_TYPES}
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={e => setFileList(e.fileList.slice(-1))}
              style={{ marginBottom: 0 }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag file here</p>
              <p className="ant-upload-hint">PDF, JPG, PNG, DOC, DOCX, XLS, XLSX · Max 10 MB</p>
            </Dragger>
          </Form.Item>

          {fileList.length > 0 && (
            <Alert
              type="success"
              showIcon
              message={`Selected: ${fileList[0].name} (${formatFileSize(fileList[0].size ?? 0)})`}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item name="branchId" label="Branch">
            <LookupSelect
              fetcher={branchFetcher}
              modalTitle="Select Branch"
              placeholder="Select the branch this document belongs to..."
              allowClear
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="entityType" label="Entity Type" rules={[{ required: true }]}>
                <Select options={ENTITY_TYPES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="entityId" label="Entity ID" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={1} placeholder="e.g. 45" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="documentType" label="Document Type" rules={[{ required: true }]}>
            <Select options={DOCUMENT_TYPES} showSearch optionFilterProp="label" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes or context..." />
          </Form.Item>
        </Form>
      </Drawer>

      {/* ── Edit Drawer ── */}
      <Drawer
        title={<Space><EditOutlined /> Edit Document Metadata</Space>}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        width={400}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setEditTarget(null)}>Cancel</Button><Button type="primary" loading={editSaving} onClick={onSaveEdit}>Save Changes</Button></div>}
      >
        {editTarget && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="File">{editTarget.fileName}</Descriptions.Item>
              <Descriptions.Item label="Entity">
                <EntityBadge entityType={editTarget.entityType} entityId={editTarget.entityId} />
              </Descriptions.Item>
              <Descriptions.Item label="Size">{formatFileSize(editTarget.fileSize)}</Descriptions.Item>
              <Descriptions.Item label="Uploaded">{dayjs(editTarget.uploadedAt).format("DD MMM YYYY")}</Descriptions.Item>
            </Descriptions>
            <Form form={editForm} layout="vertical">
              <Form.Item name="documentType" label="Document Type" rules={[{ required: true }]}>
                <Select options={DOCUMENT_TYPES} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} placeholder="Optional notes..." />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>

      {/* ── Preview Modal ── */}
      <Modal
        title={previewTarget?.fileName}
        open={!!previewTarget}
        onCancel={() => setPreviewTarget(null)}
        footer={
          previewTarget && (
            <Button icon={<DownloadOutlined />} href={previewTarget.downloadUrl} target="_blank">
              Download
            </Button>
          )
        }
        width={820}
        centered
      >
        {previewTarget && (() => {
          const ext = previewTarget.fileExtension.toLowerCase().replace(".", "");
          if (["jpg", "jpeg", "png"].includes(ext)) {
            return (
              <img
                src={previewTarget.downloadUrl}
                alt={previewTarget.fileName}
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", display: "block", margin: "0 auto" }}
              />
            );
          }
          if (ext === "pdf") {
            return (
              <iframe
                src={previewTarget.downloadUrl}
                title={previewTarget.fileName}
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
            );
          }
          return (
            <div style={{ textAlign: "center", padding: 40 }}>
              <FileUnknownOutlined style={{ fontSize: 48, color: "#999" }} />
              <div style={{ marginTop: 12 }}>Preview not available for this file type.</div>
              <Button style={{ marginTop: 12 }} icon={<DownloadOutlined />} href={previewTarget.downloadUrl} target="_blank">
                Download to view
              </Button>
            </div>
          );
        })()}
      </Modal>
    </Card>
  );
};
